import { chatRequest, loadChatHistory, saveChatMessage } from "../../api/chatApi";
import { getInitialMessages, getInitialCTAs } from "../chat.constants";
import { indexProductsFromBlocks, resolveProduct } from "../productIndex";
import { buildErrorMessage, cartItemsToMap } from "./session.helpers";
import {
  apiAddToCart,
  apiClearCart,
  apiDecrementCart,
  apiRemoveCartItem,
} from "../../api/cart.api";
import { trackEvent } from "../../services/amplitude";

export async function callChatApi({
  text,
  historyOverride,
  chatId,
  cart,
  reqSeqRef,
  setLoading,
  setMessages,
  setCtas,
  setScreen,
  setCart,
  productIndexRef,
}) {
  const myReq = ++reqSeqRef.current;
  setLoading(true);

  try {
    const data = await chatRequest({
      chat_id: chatId,
      message: text,
      history: historyOverride,
      cart,
    });

    if (myReq !== reqSeqRef.current) return;

    const allBlocks = Array.isArray(data.blocks) ? data.blocks : [];

const cartActionBlocks = allBlocks.filter(
  (b) => b?.type === "cart_action" && b?.action === "add_items"
);

const nextBlocks = allBlocks.filter(
  (b) => b?.type !== "cart_action"
);

    if (nextBlocks.length) {
      indexProductsFromBlocks(productIndexRef, nextBlocks);
    }

    const productsFromBlocks = [
  ...new Map(
    nextBlocks
      .flatMap((b) => {
        if (Array.isArray(b?.products)) return b.products;
        if (Array.isArray(b?.items)) return b.items;
        if (Array.isArray(b?.data?.products)) return b.data.products;
        return [];
      })
      .map((p) => [
        String(p?.product_id || p?.variant_id || p?.id),
        p,
      ])
  ).values(),
];

const productsTotal = nextBlocks.reduce((sum, b) => {
  if (b?.type === "product_links" || b?.type === "product_list") {
    return sum + Number(b?.total || b?.products?.length || 0);
  }

  return sum;
}, 0);

    trackEvent("message_received", {
  chat_id: chatId,
  intent: data.intent || null,

  message_length: String(data.bot_message || "").length,
  message_preview: String(data.bot_message || "").slice(0, 120),

  has_products: productsFromBlocks.length > 0,
  products_count: productsFromBlocks.length,
  products_total: productsTotal,

  product_ids: productsFromBlocks
    .map((p) => p?.product_id || p?.variant_id || p?.id)
    .filter(Boolean),

  product_names: productsFromBlocks
    .map((p) => p?.name || p?.name_uk || p?.name_en || p?.title)
    .filter(Boolean),

  products: productsFromBlocks.map((p) => ({
    product_id: p?.product_id || p?.id || null,
    variant_id: p?.variant_id || null,
    name: p?.name || p?.name_uk || p?.name_en || p?.title || null,
    price: Number(p?.price ?? 0),
  })),

  has_ctas: Array.isArray(data.ctas) ? data.ctas.length > 0 : false,
  ctas_count: Array.isArray(data.ctas) ? data.ctas.length : 0,

  source: "assistant",
});

    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        text: data.bot_message || "Ок",
        blocks: nextBlocks,
        animate: true,
      },
    ]);

    setCtas(Array.isArray(data.ctas) ? data.ctas : []);

    for (const block of cartActionBlocks) {
  const productIds = Array.isArray(block.product_ids)
    ? block.product_ids.filter(Boolean)
    : [];

  for (const productId of productIds) {
    try {
      const productFromBlock = Array.isArray(block.products)
  ? block.products.find(
      (p) =>
        String(p?.id) === String(productId) ||
        String(p?.product_id) === String(productId) ||
        String(p?.variant_id) === String(productId)
    )
  : null;

const prod = resolveProduct(productIndexRef, {
  product_id: productId,
});

const product = productFromBlock ||
  prod || {
    id: productId,
    product_id: productId,
  };

      const res = await apiAddToCart(
        {
          ...product,
          id: productId,
          product_id: productId,
        },
        Number(block.quantity || 1)
      );

      if (!res?.unauthenticated) {
        setCart(cartItemsToMap(res?.items || []));
      }
    } catch (e) {
      console.error("cart_action add_items error:", e);
    }
  }
}

    if (Array.isArray(data.actions)) {
      for (const a of data.actions) {
        if (a?.type === "go_to_checkout") {
          setScreen("checkout");
          continue;
        }

        if (a?.type === "clear_cart") {
          try {
            const res = await apiClearCart();
            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("clear_cart server action error:", e);
          }
          continue;
        }

        if (a?.type === "remove_from_cart") {
          try {
            const productId = String(a?.product_id || "");
            if (!productId) continue;

            const res = await apiRemoveCartItem(productId);
            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("remove_from_cart server action error:", e);
          }
          continue;
        }

        if (a?.type === "decrement_cart") {
          try {
            const productId = String(a?.product_id || "");
            if (!productId) continue;

            const res = await apiDecrementCart(productId, Number(a?.qty || 1));
            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("decrement_cart server action error:", e);
          }
          continue;
        }

        if (a?.type === "add_to_cart") {
          try {
            const prod = resolveProduct(productIndexRef, a);
            const product = prod || a?.product || {};
            const productId = String(
              a?.product_id ||
                product?.id ||
                product?.product_id ||
                product?.variant_id ||
                ""
            );

            if (!productId) continue;

            const res = await apiAddToCart(
              { ...product, id: productId, product_id: productId },
              Number(a?.qty || 1)
            );

            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("add_to_cart server action error:", e);
          }
        }
      }
    }
  } catch (e) {
    if (myReq !== reqSeqRef.current) return;
    setMessages((m) => [...m, buildErrorMessage()]);
  } finally {
    if (myReq === reqSeqRef.current) setLoading(false);
  }
}

export async function loadChatSession({
  externalId,
  setLoading,
  setChatId,
  setMessages,
  setCtas,
  setScreen,
  productIndexRef,
}) {
  const id = String(externalId || "").trim();
  if (!id) return;

  setLoading(true);
  try {
    const data = await loadChatHistory(id);

    const restored = (data.messages || []).map((m) => {
  const rawBlocks = m.blocks || m.meta?.blocks || [];

  const safeBlocks = Array.isArray(rawBlocks)
    ? rawBlocks.filter((b) => b?.type !== "cart_action")
    : [];

  return {
    role: m.role,
    text: m.text,
    meta: m.meta,
    blocks: safeBlocks,
    animate: false,
  };
});

    setChatId(id);
    setMessages(restored.length ? restored : getInitialMessages());

    const allBlocks = restored
  .flatMap((m) => m.blocks || [])
  .filter((b) => b?.type !== "cart_action");
    if (allBlocks.length) {
      indexProductsFromBlocks(productIndexRef, allBlocks);
    }

    setCtas(getInitialCTAs());
    setScreen("chat");
  } catch (e) {
    console.error("loadChat error:", e);
  } finally {
    setLoading(false);
  }
}

export async function syncGuestChatToServer() {
  try {
    const chatId = localStorage.getItem("guest_chat_id");
    const raw = localStorage.getItem("guest_chat_messages");

    if (!chatId || !raw) return null;

    const messages = JSON.parse(raw).filter(
      (m) => m?.role === "user" || m?.role === "assistant"
    );

    for (const message of messages) {
      if (!message?.text && !Array.isArray(message?.blocks)) continue;

      await saveChatMessage(chatId, {
        role: message.role,
        text: message.text || "",
        blocks: Array.isArray(message.blocks) ? message.blocks : [],
        meta: message.meta || {},
      });
    }

    localStorage.removeItem("guest_chat_id");
    localStorage.removeItem("guest_chat_messages");

    return chatId;
  } catch (e) {
    console.error("syncGuestChatToServer error:", e);
    return null;
  }
}